import { useEffect, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import core, { Async, GQL, View } from '@gin-betting/core';
import restrictions, { Restrictions } from '@gin-betting/restrictions';
import { keys } from 'lodash';
import type { Arg2 } from 'tsargs';

const {
    actions: { updateRestrictions },
    reducer,
    constants: { initialState },
} = restrictions;
const {
    appServiceLocator,
    entities: { normalizeEntity },
} = core;
const {
    selectors: { selectBetslipOdds },
} = betslip;

export interface RestrictionsCtx {
    state: Restrictions.RestrictionsState;
    dispatchRestrictions: (action: Arg2<typeof reducer>) => void;
}

const useRestrictions = (): RestrictionsCtx => {
    const [state, dispatchRestrictions] = useReducer(reducer, initialState);
    const betslipOdds = useSelector(selectBetslipOdds);

    const betslipOddsRef = useRef(betslipOdds);
    betslipOddsRef.current = betslipOdds;

    useEffect(() => {
        const apiClient = appServiceLocator.get('apiClient');
        const subscription = apiClient
            .subscribe({ method: 'onUpdateBet', variables: {} })
            .subscribe(({ data: { onUpdateBet } }) => {
                if (!betslipOddsRef.current.count()) return;

                const {
                    entities: { bets },
                } = normalizeEntity({
                    bets: [onUpdateBet],
                }) as Async.BetNormalized;

                const betId = keys(bets)[0];

                if (!betId) return;

                const bet = bets[betId];

                if (!bet) return;

                const { declineContext } = bet;

                const error = declineContext[0];

                if (!error) return;

                const {
                    restrictions: newRestrictions,
                } = error as GQL.BetDeclineContextRestrictions;

                const betslipOddIds = betslipOddsRef.current
                    .filter(betslipOdd => betslipOdd.betId === betId)
                    .keySeq()
                    .toSet();

                return dispatchRestrictions(
                    updateRestrictions({
                        restrictions: newRestrictions as View.Restriction[],
                        betslipOddIds,
                    })
                );
            });

        return () => {
            subscription.unsubscribe();
        };
    }, [dispatchRestrictions]);

    return {
        state,
        dispatchRestrictions,
    };
};

export default useRestrictions;
